'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Image from 'next/image';
import { useTranslations, useLocale, useMessages } from 'next-intl';
import { useAuth } from '@medizzconnect/auth';
import { getPlanFeatures } from '@medizzconnect/shared';
import { cn } from '@medizzconnect/shared';
import { SocialLinks } from './social-links';
import { FavoriteButton } from './favorite-button';
import { getCachedVideoProfile } from '@/lib/video-profile-cache';
import type { Database, SocialLinks as SocialLinksType } from '@medizzconnect/db';

type DoctorProfileRow = Database['public']['Tables']['doctor_profiles']['Row'];

interface DoctorCategory {
  id: string;
  profile_id: string;
  category_id: string;
  subcategory_name: string | null;
  treatment_name: string | null;
  sort_order: number;
  price_min: number | null;
  price_max: number | null;
  price_custom: string | null;
  created_at: string;
  category_name: string;
  category_slug: string | null;
  category_color: string | null;
  category_icon: string | null;
}

interface ClinicColleague {
  id: string;
  first_name: string;
  last_name: string;
  title: string | null;
  berufsbezeichnung: string | null;
  thumbnail_url: string | null;
  video_url: string | null;
  processed_video_url: string | null;
}

interface ClinicInfo {
  name: string | null;
  logoUrl: string | null;
  introVideoUrl: string | null;
  introThumbnailUrl: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  description: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  adminHookline: string | null;
  adminDescription: string | null;
  adminProfileId: string | null;
  adminCategories: DoctorCategory[];
  colleagues: ClinicColleague[];
}

interface VideoProfileData extends DoctorProfileRow {
  plan: string;
  categories: DoctorCategory[];
  clinic: ClinicInfo | null;
  active_addons?: string[];
}

interface VideoModalProps {
  profileId: string;
  onClose: () => void;
}

/**
 * Video-Modal — V1-Style Profil-Ansicht.
 * Profilbild, Video, Kontakt, Kategorien, Praxis-Logo
 */
export function VideoModal({ profileId, onClose }: VideoModalProps) {
  const t = useTranslations('doctor');
  const tModal = useTranslations('videoModal');
  const tCommon = useTranslations('common');
  const locale = useLocale();
  const isArabicLocale = locale?.toLowerCase().startsWith('ar');
  const { userId } = useAuth();
  const langNames = (useMessages().languageNames as Record<string, string>) ?? {};
  const langDisplayName = (lang: string) => langNames[lang] ?? lang;

  const [currentProfileId, setCurrentProfileId] = useState(profileId);
  const [profile, setProfile] = useState<VideoProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const [isClinicView, setIsClinicView] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const copyShareLink = useCallback(() => {
    const localePrefix = locale === 'de' ? '' : `/${locale}`;
    const url = `${window.location.origin}${localePrefix}/doctors/${currentProfileId}`;
    navigator.clipboard.writeText(url).then(() => {
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 2500);
    });
  }, [locale, currentProfileId]);

  // Profil laden
  useEffect(() => {
    let cancelled = false;
    setError(false);

    // 1. Cache prüfen (vom VideoCard-Hover vorgeladen)
    const cached = getCachedVideoProfile(currentProfileId, locale);

    if (cached && 'data' in cached && cached.ready && cached.data) {
      // Sofort anzeigen – kein Spinner
      setProfile(cached.data as VideoProfileData);
      setLoading(false);
      return;
    }

    setLoading(true);

    // 2. Falls Fetch noch läuft → auf laufendes Promise warten
    const fetchPromise =
      cached && 'promise' in cached
        ? cached.promise
        : fetch(`/api/videos/${currentProfileId}?locale=${locale}`)
            .then((res) => {
              if (!res.ok) throw new Error('Not found');
              return res.json();
            });

    fetchPromise
      .then((data) => {
        if (!cancelled && data) {
          setProfile(data as VideoProfileData);
          setLoading(false);
        } else if (!data) {
          throw new Error('No data');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [currentProfileId, locale]);

  // Escape + Body scroll lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  }, []);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play();
        setIsVideoPlaying(true);
      } else {
        video.pause();
        setIsVideoPlaying(false);
      }
    }
  }, []);

  // Klinik-Video Feature: Alle Mitglieder der Klinik (inkl. aktuelles Profil)
  const clinicMembers = useMemo(() => {
    if (!profile?.clinic?.colleagues) return [];
    const current = {
      id: currentProfileId,
      first_name: profile.first_name ?? '',
      last_name: profile.last_name ?? '',
      title: profile.title,
      berufsbezeichnung: profile.berufsbezeichnung,
      thumbnail_url: profile.thumbnail_url,
      video_url: profile.video_url,
      processed_video_url: profile.processed_video_url ?? null,
    };
    return [current, ...profile.clinic.colleagues];
  }, [profile, currentProfileId]);

  // Bedingung: Klinik-Tabs sichtbar = Klinik vorhanden UND 1+ Mitglieder mit Video
  const isClinicVideoEnabled = useMemo(() => {
    if (!profile?.clinic) return false;
    const membersWithVideo = clinicMembers.filter(m => m.video_url || m.processed_video_url).length;
    return membersWithVideo >= 1;
  }, [profile, clinicMembers]);

  // Kategorien gruppiert nach subcategory_name
  const groupedCategories = useMemo(() => {
    if (!profile?.categories) return [];
    const groups = new Map<string, DoctorCategory[]>();
    for (const cat of profile.categories) {
      const key = cat.subcategory_name || cat.category_name;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(cat);
    }
    return Array.from(groups.entries()).map(([name, items]) => ({
      subcategory: name,
      parentCategory: items[0]?.category_name ?? '',
      treatments: items,
    }));
  }, [profile?.categories]);

  // Admin-Kategorien für Klinik-View
  const adminGroupedCategories = useMemo(() => {
    if (!profile?.clinic?.adminCategories) return [];
    const groups = new Map<string, DoctorCategory[]>();
    for (const cat of profile.clinic.adminCategories) {
      const key = cat.subcategory_name || cat.category_name;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(cat);
    }
    return Array.from(groups.entries()).map(([name, items]) => ({
      subcategory: name,
      parentCategory: items[0]?.category_name ?? '',
      treatments: items,
    }));
  }, [profile?.clinic?.adminCategories]);

  // Loading
  if (loading) {
    return (
      <div
        className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={handleBackdropClick}
      >
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/30 border-t-white" />
          <span className="text-sm text-white/80">{tCommon('loading')}</span>
        </div>
      </div>
    );
  }

  // Error
  if (error || !profile) {
    return (
      <div
        className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={handleBackdropClick}
      >
        <div className="bg-white p-8 text-center shadow-2xl">
          <p className="text-gray-600">{tCommon('error')}</p>
          <button onClick={onClose} className="mt-4 bg-primary px-4 py-2 text-sm text-white">
            {tCommon('close')}
          </button>
        </div>
      </div>
    );
  }

  const features = getPlanFeatures(profile.plan ?? 'BASIS', profile.active_addons);
  const social = (profile.social_links ?? {}) as SocialLinksType;
  const displayTitle = profile.title && profile.title !== 'Kein Titel' ? profile.title : '';
  const fullName = `${displayTitle} ${profile.first_name} ${profile.last_name}`.trim();
  const locationParts = [profile.city, profile.country].filter(Boolean);
  const fullAddress = [profile.address, profile.postal_code, profile.city, profile.country].filter(Boolean).join(', ');
  const clinicLocationParts = profile.clinic ? [profile.clinic.city, profile.clinic.country].filter(Boolean) : [];
  const clinicFullAddress = profile.clinic ? [profile.clinic.street, profile.clinic.postalCode, profile.clinic.city, profile.clinic.country].filter(Boolean).join(', ') : '';
  const emailSubject = 'Kontaktanfrage über MedizzConnect';
  const emailBody = ['Guten Tag,', '', 'Ich kontaktiere Sie über MedizzConnect.', '', 'Mit freundlichen Grüßen'].join('\n');
  const mailtoHref = `mailto:${profile.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

  const formatPrice = (cat: DoctorCategory) => {
    if (cat.price_custom) return cat.price_custom;
    if (cat.price_min != null && cat.price_max != null) return `${cat.price_min}–${cat.price_max} €`;
    if (cat.price_min != null) return `ab ${cat.price_min} €`;
    if (cat.price_max != null) return `bis ${cat.price_max} €`;
    return t('onRequest');
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm p-2 sm:p-4 md:p-6"
      onClick={handleBackdropClick}
    >
      <div
        className="relative w-full max-w-3xl bg-white shadow-2xl overflow-hidden my-4 animate-in fade-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
        aria-label={fullName}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow-md backdrop-blur-sm transition hover:bg-white hover:text-gray-900"
          aria-label={tCommon('close')}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
{/* Header */}
<div className="border-b border-gray-100 px-5 py-3 sm:px-6">
  {/* FIX: pt-12, -mt-8 und pb-2 geben dem Tooltip im Scrollbereich den nötigen Platz nach oben */}
  <div className="flex items-center gap-3 overflow-x-auto pt-12 -mt-8 pb-2">

    {/* Name / Share-Link */}
    <div className="relative group flex-shrink-0">
      <button
        onClick={() => {
          if (isClinicVideoEnabled) {
            setIsClinicView(false);
          } else {
            copyShareLink();
          }
        }}
        className={cn(
          'text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0',
          isClinicVideoEnabled && !isClinicView
            ? 'text-primary border-b-2 border-primary pb-0.5'
            : isClinicVideoEnabled
              ? 'text-gray-400 hover:text-gray-600'
              : 'text-primary hover:underline'
        )}
      >
        {fullName}
      </button>

      {/* Tooltip */}
      <div className="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150">
        <div className="relative whitespace-nowrap rounded px-2 py-1 text-xs text-white bg-teal-900 shadow-lg">
          {isClinicVideoEnabled ? fullName : 'Link kopieren'}
          {/* Pfeil */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-teal-900" />
        </div>
      </div>
    </div>

    {/* Klinik + Mitglieder */}
    {isClinicVideoEnabled && profile?.clinic && (
      <>
        {/* Trennlinie */}
        <div className="h-5 w-px bg-gray-200 flex-shrink-0" />

        {/* Klinik-Tab */}
        <div className="relative group flex-shrink-0">
          <button
            onClick={() => setIsClinicView(true)}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full transition-all flex-shrink-0',
              isClinicView
                ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            )}
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <span className="max-w-[100px] truncate">
              {profile.clinic.name}
            </span>
          </button>

          {/* Tooltip */}
          <div className="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150">
            <div className="relative whitespace-nowrap rounded px-2 py-1 text-xs text-white bg-teal-900 shadow-lg">
              {profile.clinic.name ?? 'Klinik'}
              {/* Pfeil */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-teal-900" />
            </div>
          </div>
        </div>

        {/* Mitglieder */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {clinicMembers
            .filter(m => m.video_url || m.processed_video_url)
            .map((member) => (
              <div key={member.id} className="relative group flex-shrink-0">

                {/* Button */}
                <button
                  onClick={() => {
                    setIsClinicView(false);
                    if (member.id !== currentProfileId) {
                      setCurrentProfileId(member.id);
                      setShowFullDescription(false);
                    }
                  }}
                  className={cn(
                    'transition-all focus:outline-none',
                    !isClinicView && member.id === currentProfileId
                      ? 'ring-2 ring-primary ring-offset-1 rounded-full'
                      : 'opacity-70 hover:opacity-100'
                  )}
                >
                  <div className="relative h-7 w-7 overflow-hidden rounded-full bg-gray-200">
                    {member.thumbnail_url ? (
                      <Image
                        src={member.thumbnail_url}
                        alt={`${member.first_name} ${member.last_name}`}
                        fill
                        className="object-cover"
                        sizes="28px"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[10px] font-bold text-gray-400">
                        {member.first_name?.[0]}
                        {member.last_name?.[0]}
                      </div>
                    )}
                  </div>
                </button>

                {/* Tooltip */}
                <div className="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150">
                  <div className="relative whitespace-nowrap rounded px-2 py-1 text-xs text-white bg-teal-900 shadow-lg">
                    {member.title && member.title !== 'Kein Titel'
                      ? `${member.title} `
                      : ''}
                    {member.first_name} {member.last_name}

                    {/* Pfeil */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-teal-900" />
                  </div>
                </div>

              </div>
            ))}
        </div>
      </>
    )}
  </div>
</div>
        {/* Kopiert-Toast */}
        {showCopiedToast && (
          <div className="absolute top-14 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-gray-900 text-white px-4 py-2 text-sm shadow-lg animate-in fade-in slide-in-from-top-2 duration-200">
            <svg className="h-4 w-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Link kopiert!
          </div>
        )}

        <div className="flex flex-col lg:flex-row">
          {/* ════════════ LINKE SPALTE ════════════ */}

          <div className="flex-1 min-w-0 p-5 sm:p-6">
            <div className="mx-auto max-w-md space-y-5">
                       {/* Video Player */}
            {(isClinicView ? (profile.clinic?.introVideoUrl || profile.clinic?.logoUrl) : (profile.processed_video_url ?? profile.video_url)) && (
              <div className="relative overflow-hidden bg-gray-900 w-full" style={{ aspectRatio: '4/3' }}>
                {isClinicView && !profile.clinic?.introVideoUrl ? (
                  /* Klinik-Platzhalter wenn kein Intro-Video vorhanden */
                  <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 text-center p-6">
                    {profile.clinic?.logoUrl ? (
                      <div className="relative h-20 w-20 mb-4 overflow-hidden rounded-lg">
                        <Image
                          src={profile.clinic.logoUrl}
                          alt={profile.clinic?.name ?? 'Klinik'}
                          fill
                          className="object-contain"
                          sizes="80px"
                        />
                      </div>
                    ) : (
                      <svg className="h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                    <h3 className="text-lg font-bold text-gray-900">{profile.clinic?.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{clinicMembers.filter(m => m.video_url || m.processed_video_url).length} Team-Mitglieder</p>
                  </div>
                ) : (
                <>
                <video
                  ref={videoRef}
                  src={isClinicView ? (profile.clinic?.introVideoUrl ?? '') : (profile.processed_video_url ?? profile.video_url ?? '')}
                  poster={isClinicView ? (profile.clinic?.introThumbnailUrl ?? undefined) : (profile.thumbnail_url ?? undefined)}
                  className="h-full w-full object-cover cursor-pointer"
                  loop
                  playsInline
                  autoPlay
                  disablePictureInPicture
                  disableRemotePlayback
                  controlsList="nodownload noremoteplayback"
                  onContextMenu={(e) => e.preventDefault()}
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                  onClick={togglePlay}
                />
                <button
                  onClick={toggleMute}
                  className="absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition hover:bg-black/70"
                >
                  {isMuted ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                  )}
                </button>
                </>
                )}
              </div>
            )}
            {/* Profilbild + Name */}
            <div className="flex flex-col sm:flex-row gap-5">

              {/* Klinik-Ansicht: Klinik-Name + Branding */}
              {isClinicView && profile.clinic ? (
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-start gap-3">
                    {profile.clinic.logoUrl && (
                      <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden border border-gray-200 bg-white rounded">
                        <Image
                          src={profile.clinic.logoUrl}
                          alt={profile.clinic.name ?? 'Klinik'}
                          fill
                          className="object-contain"
                          sizes="48px"
                        />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-gray-900">{profile.clinic.name}</h2>
                        <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold text-white bg-primary rounded">
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          {tModal('clinicBadge')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {clinicMembers.filter(m => m.video_url || m.processed_video_url).length} Ärzte{profile.clinic?.introVideoUrl ? ' • Klinik-Vorstellung' : ''}
                      </p>
                    </div>
                  </div>

                  {(clinicLocationParts.length > 0 || locationParts.length > 0) && (
                    <p className="flex items-center gap-1.5 text-sm text-gray-500">
                      <svg className="h-4 w-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {clinicFullAddress || clinicLocationParts.join(', ') || fullAddress || locationParts.join(', ')}
                    </p>
                  )}

                  {/* Klinik-Beschreibung */}
                  {profile.clinic?.description && (
                    <p className="text-sm text-gray-600 leading-relaxed mt-2">{profile.clinic.description}</p>
                  )}
                </div>
              ) : (
                /* Name & Info — normale Arzt-Ansicht */
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-start gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-gray-900">{fullName}</h2>
                    {features.hasPremiumBadge && (
                      <span
                        className="flex-shrink-0 px-2 py-0.5 text-xs font-semibold text-white"
                        style={{ background: 'linear-gradient(135deg, #38b2ac, #2c7a7b)' }}
                      >
                        {features.label}
                      </span>
                    )}
                  </div>

                  {profile.berufsbezeichnung && (
                    <p className="text-sm font-medium text-gray-600">{profile.berufsbezeichnung}</p>
                  )}

                  {locationParts.length > 0 && (
                    <p className="flex items-center gap-1.5 text-sm text-gray-500">
                      <svg className="h-4 w-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {fullAddress || locationParts.join(', ')}
                    </p>
                  )}

                  {profile.video_title && profile.video_title !== 'Profil' && (
                    <p className="text-sm text-gray-600">{profile.video_title}</p>
                  )}
                </div>
              )}
            </div>

 

            {/* Hookline & Description */}
            {isClinicView && profile.clinic ? (
              /* Klinik-View: Admin-Hookline + Admin-Beschreibung */
              (profile.clinic.adminHookline || profile.clinic.adminDescription) ? (
                <div className="bg-gray-50 p-4 space-y-2">
                  {profile.clinic.adminHookline && (
                    <h3 className="text-base font-bold text-gray-900">{profile.clinic.adminHookline}</h3>
                  )}
                  {profile.clinic.adminDescription && (
                    <div>
                      <p className={cn(
                        'text-sm leading-relaxed text-gray-600 whitespace-pre-line',
                        !showFullDescription && 'line-clamp-5'
                      )}>
                        {profile.clinic.adminDescription}
                      </p>
                      {profile.clinic.adminDescription.length > 150 && (
                        <button
                          onClick={() => setShowFullDescription(!showFullDescription)}
                          className="mt-2 text-sm font-medium text-primary hover:underline"
                        >
                          {showFullDescription ? tCommon('showLess') : tModal('more')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : null
            ) : (features.hasHookline && profile.hookline) || (features.hasDescription && profile.description) ? (
              <div className="bg-gray-50 p-4 space-y-2">
                {features.hasHookline && profile.hookline && (
                  <h3 className="text-base font-bold text-gray-900">{profile.hookline}</h3>
                )}
                {features.hasDescription && profile.description && (
                  <div>
                    <p className={cn(
                      'text-sm leading-relaxed text-gray-600 whitespace-pre-line',
                      !showFullDescription && 'line-clamp-5'
                    )}>
                      {profile.description}
                    </p>
                    {profile.description.length > 150 && (
                      <button
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        className="mt-2 text-sm font-medium text-primary hover:underline"
                      >
                        {showFullDescription ? tCommon('showLess') : tModal('more')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : null}

            {/* Kategorien (V1-Style Cards) */}
            {(isClinicView ? adminGroupedCategories : groupedCategories).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">{tModal('treatments')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(isClinicView ? adminGroupedCategories : groupedCategories).map((group) =>
                    group.treatments.map((cat) => (
                      <div
                        key={cat.id}
                        className="bg-white border border-gray-200 rounded-lg p-3 hover:border-primary/30 hover:bg-primary/5 transition-colors"
                      >
                        <div className="min-w-0">
                          <div>
                            <p className="text-[15px] leading-snug font-semibold text-gray-900 break-words hyphens-auto" lang="de">
                              {cat.treatment_name || cat.subcategory_name || cat.category_name}
                            </p>
                            {cat.subcategory_name && cat.subcategory_name !== cat.treatment_name && (
                              <p className="text-sm text-gray-500 break-words hyphens-auto mt-0.5" lang="de">
                                {cat.subcategory_name}
                              </p>
                            )}
                          </div>
                          <span
                            className={cn(
                              'inline-flex mt-2 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 whitespace-nowrap',
                              isArabicLocale ? 'self-start' : 'self-end'
                            )}
                          >
                            {formatPrice(cat)}
                          </span>
                        </div>
                        {cat.category_name && (
                          <span className="inline-block px-2 py-0.5 bg-blue-50 text-teal-700 text-xs rounded-full mt-2 w-fit">
                            {cat.category_name}
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
            </div>
          </div>

          {/* ════════════ RECHTE SPALTE ════════════ */}
          <div className="w-full lg:w-80 flex-shrink-0 border-t lg:border-t-0 lg:border-l border-gray-100 p-5 sm:p-6 space-y-5">
            {/* Kontakt */}
            <div className="space-y-2.5">
              <h3 className="text-sm font-semibold text-gray-900">{t('contact')}</h3>

              {isClinicView && profile.clinic ? (
                /* Klinik-Kontaktdaten */
                <>
                  {profile.clinic.phone && (
                    <a
                      href={`tel:${profile.clinic.phone}`}
                      className="flex items-center gap-2.5 border border-gray-200 px-4 py-2.5 text-sm text-gray-700 transition hover:border-primary hover:text-primary"
                    >
                      <svg className="h-4 w-4 flex-shrink-0 text-primary" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {profile.clinic.phone}
                    </a>
                  )}
                  {profile.clinic.email && (
                    <a
                      href={`mailto:${profile.clinic.email}`}
                      className="flex items-center gap-2.5 border border-gray-200 px-4 py-2.5 text-sm text-gray-700 transition hover:border-primary hover:text-primary"
                    >
                      <svg className="h-4 w-4 flex-shrink-0 text-primary" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="truncate">{profile.clinic.email}</span>
                    </a>
                  )}
                  {profile.clinic.website && (
                    <a
                      href={profile.clinic.website.startsWith('http') ? profile.clinic.website : `https://${profile.clinic.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2.5 border border-gray-200 px-4 py-2.5 text-sm text-gray-700 transition hover:border-primary hover:text-primary"
                    >
                      <svg className="h-4 w-4 flex-shrink-0 text-primary" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                      </svg>
                      <span className="truncate">{profile.clinic.website}</span>
                    </a>
                  )}
                </>
              ) : (
                /* Arzt-Kontaktdaten */
                <>
              {features.hasPhone && profile.phone && (
                features.activeContact ? (
                  <a
                    href={`tel:${profile.phone}`}
                    className="flex items-center gap-2.5 border border-gray-200 px-4 py-2.5 text-sm text-gray-700 transition hover:border-primary hover:text-primary"
                  >
                    <svg className="h-4 w-4 flex-shrink-0 text-primary" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {profile.phone}
                  </a>
                ) : (
                  <div className="flex items-center gap-2.5 border border-gray-100 bg-gray-50 px-4 py-2.5 text-sm text-gray-400">
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {profile.phone}
                  </div>
                )
              )}

              {features.hasEmail && profile.email && (
                features.activeContact ? (
                  <a
                    href={mailtoHref}
                    className="flex items-center gap-2.5 border border-gray-200 px-4 py-2.5 text-sm text-gray-700 transition hover:border-primary hover:text-primary"
                  >
                    <svg className="h-4 w-4 flex-shrink-0 text-primary" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="truncate">{profile.email}</span>
                  </a>
                ) : (
                  <div className="flex items-center gap-2.5 border border-gray-100 bg-gray-50 px-4 py-2.5 text-sm text-gray-400">
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="truncate">{profile.email}</span>
                  </div>
                )
              )}

              {features.hasWebsite && profile.website && (
                <a
                  href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 border border-gray-200 px-4 py-2.5 text-sm text-gray-700 transition hover:border-primary hover:text-primary"
                >
                  <svg className="h-4 w-4 flex-shrink-0 text-primary" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                  </svg>
                  <span className="truncate">{profile.website}</span>
                </a>
              )}
                </>
              )}
            </div>

     {/* Praxis / Logo */}
            {features.hasLogo && profile.logo_url && (
              <div className="border border-gray-200 p-4 space-y-3">
                <div className="relative h-24 w-full">
                  <Image
                    src={profile.logo_url}
                    alt={`Logo ${fullName}`}
                    fill
                    className="object-contain object-center"
                    sizes="250px"
                  />
                </div>
                {profile.image_url && (
                  <div className="relative h-36 w-full overflow-hidden">
                    <Image
                      src={profile.image_url}
                      alt={fullName}
                      fill
                      className="object-cover"
                      sizes="250px"
                    />
                  </div>
                )}
                {locationParts.length > 0 && (
                  <p className="flex items-center gap-1.5 text-xs text-gray-500">
                    <svg className="h-3.5 w-3.5 flex-shrink-0 text-primary" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {fullAddress || locationParts.join(', ')}
                  </p>
                )}
              </div>
            )}

            {/* Klinik-Banner {profile.clinic && profile.clinic.name && (
              <div className="border border-primary/20 bg-primary/5 p-4 space-y-3">
                <div className="flex items-center gap-2.5">
                  {profile.clinic.logoUrl && (
                    <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden border border-gray-200 bg-white">
                      <Image
                        src={profile.clinic.logoUrl}
                        alt={profile.clinic.name}
                        fill
                        className="object-contain"
                        sizes="40px"
                      />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">{tModal('partOfClinic')}</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">{profile.clinic.name}</p>
                  </div>
                </div>

                {profile.clinic.colleagues.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-medium text-gray-500">{tModal('colleagues')}</p>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {profile.clinic.colleagues.map((colleague) => (
                        <button
                          key={colleague.id}
                          onClick={(e) => { e.stopPropagation(); setCurrentProfileId(colleague.id); setShowFullDescription(false); }}
                          className="group flex flex-shrink-0 flex-col items-center gap-1 p-1.5 hover:bg-white/80 transition-colors"
                          title={`${colleague.title && colleague.title !== 'Kein Titel' ? colleague.title + ' ' : ''}${colleague.first_name} ${colleague.last_name}`}
                        >
                          <div className="relative h-9 w-9 overflow-hidden rounded-full bg-gray-200 ring-2 ring-white">
                            {colleague.thumbnail_url ? (
                              <Image src={colleague.thumbnail_url} alt={`${colleague.first_name} ${colleague.last_name}`} fill className="object-cover" sizes="36px" />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-xs font-bold text-gray-400">
                                {colleague.first_name?.[0]}{colleague.last_name?.[0]}
                              </div>
                            )}
                          </div>
                          <span className="max-w-[56px] truncate text-[10px] text-gray-500 group-hover:text-gray-700">
                            {colleague.first_name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
*/}
            
            {/* Abrechnungsarten */}
            {features.hasInsuranceInfo && profile.insurance_types && profile.insurance_types.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('insuranceTypes')}</h3>
                <div className="flex flex-wrap gap-1.5">
                  {profile.insurance_types.map((ins, i) => (
                    <span
                      key={i}
                      className={cn(
                        'inline-flex items-center px-3 py-1 text-xs font-medium',
                        ins === 'gesetzlich'
                          ? 'bg-green-100 text-green-800'
                          : ins === 'privat'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800',
                      )}
                    >
                      {ins === 'gesetzlich'
                        ? t('publicInsurance')
                        : ins === 'privat'
                          ? t('privateInsurance')
                          : ins === 'selbstzahler'
                            ? t('selfPayInsurance')
                            : ins === 'beide'
                              ? t('bothInsurance')
                              : ins}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Sprachen */}
            {profile.languages && profile.languages.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('languages')}</h3>
                <div className="flex flex-wrap gap-1.5">
                  {profile.languages.map((lang, i) => (
                    <span key={i} className="inline-flex items-center bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                      {langDisplayName(lang)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Social Media */}
            {features.hasSocialMedia && (
              <SocialLinks
                facebook={social.facebook}
                instagram={social.instagram}
                linkedin={social.linkedin}
                youtube={social.youtube}
                tiktok={social.tiktok}
                twitter={social.twitter}
                whatsapp={social.whatsapp}
              />
            )}

            {/* WhatsApp CTA */}
            {features.hasWhatsApp && social.whatsapp && features.activeContact && (
              <a
                href={`https://wa.me/${social.whatsapp.replace(/[^0-9+]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 bg-green-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp
              </a>
            )}

            {/* Termin buchen */}
            {features.hasBookingUrl && profile.booking_url && (
              <a
                href={profile.booking_url.startsWith('http') ? profile.booking_url : `https://${profile.booking_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 bg-primary px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t('bookAppointment')}
              </a>
            )}

            {/* Merken (Favorit) */}
            <FavoriteButton
              profileId={currentProfileId}
              userId={userId}
              className="w-full justify-center"
            />

            {/* Link teilen */}
            <button
              onClick={copyShareLink}
              className="flex w-full items-center justify-center gap-2 border border-gray-200 px-4 py-2.5 text-sm text-gray-600 transition hover:border-primary hover:text-primary"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              {tModal('share')}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3 sm:px-6" hidden>
          <div className="flex items-center gap-3 text-xs text-gray-400" hidden>
            <span hidden>{profile.view_count ?? 0} {t('views')}</span>
            <span className="uppercase tracking-wide" >{features.label}</span>
          </div>
          <span className="text-sm font-medium text-gray-400" hidden>
            {tModal('viewFullProfile')}
          </span>
        </div>
      </div>
    </div>
  );
}
